// Minimal hash-based history sync — no router library. The SPA's "view" is
// mirrored 1:1 into `location.hash` so the browser Back/Forward buttons walk
// through app views instead of leaving the site.
//
// Route map:
//   #/pipeline                     #/quotations/<id>
//   #/admin/<users|verticals>      #/share/<token>
//   #/admin/catalog?category=<name|__uncategorized__>  (see CatalogQuery)
//   #/search?q=<query>             (global search, see SearchPage.svelte)
//
// #/share/<token> is special: it's the public, unauthenticated view (see
// PublicShareView.svelte) and App.svelte renders it before the login gate,
// regardless of auth state — everything else here requires a session.
//
// Convention used everywhere this module is consumed: user-triggered
// navigation (nav click, opening a quote, back-to-pipeline) calls `push`,
// one history entry per view change. Programmatic redirects (post-login
// landing, logout, bouncing off a stale/invalid id) call `replace` so Back
// can't get trapped in a loop.

export type TabId = 'pipeline' | 'quotations' | 'projects' | 'admin' | 'share' | 'search' | 'catalogue';

import { decodeCatalogRouteQuery, encodeCatalogRouteQuery } from './catalog-route-codec';

// Catalog workspace selection and product filters are carried on
// #/admin/catalog so family/category views are bookmarkable and browser
// Back restores the same product workspace.
export type CatalogQuery = import('./catalog-route-codec').CatalogRouteQuery;
import { decodeCataloguePath, encodeCataloguePath, type CatalogueScreen } from './catalogue-route-codec';
import { decodePrivateFileHash, privateFileHash } from './catalogue-route-codec';

type RouteTab =
  | { tab: 'projects'; id?: number | 'none' }
  | { tab: 'quotations'; id?: number }
  | { tab: 'admin'; sub?: string; catalog?: CatalogQuery }
  | { tab: 'share'; token: string }
  | { tab: 'search'; q?: string }
  | { tab: 'pipeline' }
  | { tab: 'catalogue'; sub?: CatalogueScreen; category?: string; file?: { asset: number; revision: number } };

// `vertical` (`'all'` or a vertical slug) is the pipeline's vertical filter
// — carried as a `?vertical=` query param so it's shareable/bookmarkable and
// survives reload.
export type Route = RouteTab & { vertical?: string };

function toHash(r: Route): string {
  if (r.tab === 'catalogue') return r.file ? privateFileHash(r.file.asset, r.file.revision) : encodeCataloguePath(r.sub, r.category, r.vertical);
  let path: string;
  if (r.tab === 'quotations') path = r.id ? `#/quotations/${r.id}` : '#/quotations';
  else if (r.tab === 'projects') path = r.id ? `#/projects/${r.id}` : '#/projects';
  else if (r.tab === 'admin') path = r.sub ? `#/admin/${r.sub}` : '#/admin';
  else if (r.tab === 'share') path = `#/share/${r.token}`;
  else if (r.tab === 'search') path = '#/search';
  else path = '#/pipeline';

  const params = new URLSearchParams();
  if (r.vertical) params.set('vertical', r.vertical);
  if (r.tab === 'search' && r.q) params.set('q', r.q);
  if (r.tab === 'admin' && r.sub === 'catalog' && r.catalog) {
    for (const [key, value] of encodeCatalogRouteQuery(r.catalog)) params.set(key, value);
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

function parseHash(hash: string): Route {
  const file = decodePrivateFileHash(hash);
  if (file) return { tab: 'catalogue', file };
  const raw = hash.replace(/^#\/?/, '');
  const [pathPart, queryPart] = raw.split('?');
  const parts = pathPart.split('/').filter(Boolean);
  const [tab, sub] = parts;
  const params = queryPart ? new URLSearchParams(queryPart) : undefined;
  const vertical = params?.get('vertical') ?? undefined;

  if (tab === 'projects') return { tab: 'projects', id: sub === 'none' ? 'none' : sub && Number.isSafeInteger(Number(sub)) && Number(sub) > 0 ? Number(sub) : undefined, vertical };

  if (tab === 'quotations') {
    const id = sub ? Number(sub) : undefined;
    return { tab: 'quotations', id: id !== undefined && Number.isFinite(id) ? id : undefined, vertical };
  }
  if (tab === 'admin') {
    const adminSub = sub || undefined;
    let catalog: CatalogQuery | undefined;
    if (adminSub === 'catalog' && params) {
      catalog = decodeCatalogRouteQuery(params);
    }
    return { tab: 'admin', sub: adminSub, catalog, vertical };
  }
  if (tab === 'share' && sub) return { tab: 'share', token: sub };
  if (tab === 'search') return { tab: 'search', q: params?.get('q') ?? undefined, vertical };
  if (tab === 'catalogue') return { tab: 'catalogue', sub: decodeCataloguePath(hash).screen, ...(decodeCataloguePath(hash).category ? { category: decodeCataloguePath(hash).category } : {}), vertical };
  return { tab: 'pipeline', vertical };
}

class RouteState {
  current = $state<Route>(parseHash(location.hash));

  // A focused editor can prevent accidental sidebar/Back navigation when
  // there are unsaved changes. Only one editor exists at a time.
  private navigationGuard: (() => boolean) | null = null;
  guardNavigation(guard: () => boolean): () => void {
    this.navigationGuard = guard;
    return () => { if (this.navigationGuard === guard) this.navigationGuard = null; };
  }
  canNavigate() { return !this.navigationGuard || this.navigationGuard(); }

  // Plain navigation constructs a `Route` without a `vertical` — carry the
  // currently-selected filter forward by default. Callers that are
  // themselves changing the filter (the pipeline's vertical chips) pass
  // `vertical` explicitly and that wins.
  private withCarriedVertical(r: Route): Route {
    return r.vertical !== undefined ? r : { ...r, vertical: this.current.vertical };
  }

  // User-initiated navigation — one new history entry per view change.
  push(r: Route) {
    const merged = this.withCarriedVertical(r);
    if (toHash(merged) !== toHash(this.current) && this.navigationGuard && !this.navigationGuard()) return;
    this.current = merged;
    const hash = toHash(merged);
    if (location.hash !== hash) history.pushState(null, '', hash);
  }

  // Programmatic redirects (post-login landing, logout, invalid/stale
  // routes) — replaces the current entry so Back can't loop onto a dead view.
  replace(r: Route) {
    const merged = this.withCarriedVertical(r);
    if (toHash(merged) !== toHash(this.current) && this.navigationGuard && !this.navigationGuard()) return;
    this.current = merged;
    const hash = toHash(merged);
    if (location.hash !== hash) history.replaceState(null, '', hash);
  }
}

export const route = new RouteState();

// Browser Back/Forward (popstate) and any direct hash edits (hashchange) —
// re-parse from location.hash rather than trusting the event, since both
// fire for the same "the fragment changed under us" case.
function syncFromLocation() {
  const incoming = parseHash(location.hash);
  if (toHash(incoming) !== toHash(route.current) && !route.canNavigate()) {
    history.pushState(null, '', toHash(route.current));
    return;
  }
  route.current = incoming;
}
window.addEventListener('popstate', syncFromLocation);
window.addEventListener('hashchange', syncFromLocation);

// Self-check: toHash/parseHash must round-trip, including the catalog
// query's uncategorized sentinel and special characters in a category name.
// Dev-only (import.meta.env.DEV) so it never runs in the production bundle.
if (import.meta.env.DEV) {
  const cases: Route[] = [
    { tab: 'pipeline', vertical: 'residential' },
    { tab: 'admin', sub: 'catalog' },
    { tab: 'admin', sub: 'catalog', catalog: { category: '__uncategorized__' } },
    { tab: 'admin', sub: 'catalog', catalog: { family: 'lighting', view: 'products', q: 'dali' } },
    { tab: 'admin', sub: 'catalog', catalog: { category: 'Lighting & Controls / RCU' } },
    { tab: 'admin', sub: 'catalog', catalog: { ownership: 'none' } },
    { tab: 'admin', sub: 'catalog', catalog: { missingTaxonomy: true } },
    { tab: 'admin', sub: 'catalog', catalog: { active: false } },
    { tab: 'admin', sub: 'catalog', catalog: { all: true } },
    { tab: 'admin', sub: 'catalog', catalog: { q: 'foo bar' } },
    { tab: 'search' },
    { tab: 'search', q: 'aurora hotel' }
  ];
  for (const c of cases) {
    const roundTripped = parseHash(toHash(c));
    console.assert(
      JSON.stringify(roundTripped) === JSON.stringify(c),
      'route: toHash/parseHash round-trip failed',
      c,
      roundTripped
    );
  }
}
